/**
 * Markdown 可视化编辑（旧 components/common/MarkdownVisualEditor.vue）：
 * 所见即所得编辑，工具栏只有加粗、斜体、引用；对外读写的仍是 Markdown 文本。
 */
import { useEffect, useRef } from 'react'
import TurndownService from 'turndown'
import DOMPurify from 'dompurify'
import { normalizeMarkdownEmphasisSpacing } from '@/utils/markdownText'
import { renderMarkdown } from './markdown'
import styles from './MarkdownVisualEditor.module.css'

const turndown = new TurndownService({ headingStyle: 'atx', bulletListMarker: '-' })

function toHtml(source: string) {
  return renderMarkdown(normalizeMarkdownEmphasisSpacing((source || '').trim()))
}

function toMarkdown(html: string) {
  const cleaned = DOMPurify.sanitize(html || '')
  if (!cleaned || cleaned === '<br>' || cleaned === '<p><br></p>') return ''
  return turndown.turndown(cleaned).trim()
}

interface MarkdownVisualEditorProps {
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
}

export function MarkdownVisualEditor({ value = '', onChange, placeholder }: Readonly<MarkdownVisualEditorProps>) {
  const editorRef = useRef<HTMLDivElement>(null)
  const focusedRef = useRef(false)

  // 外部值变化时重写编辑区；正在输入时不覆盖，避免光标跳动
  useEffect(() => {
    if (editorRef.current && !focusedRef.current) {
      editorRef.current.innerHTML = toHtml(value)
    }
  }, [value])

  const emitMarkdown = () => {
    if (editorRef.current) onChange?.(toMarkdown(editorRef.current.innerHTML))
  }

  const format = (command: 'bold' | 'italic' | 'formatBlock', argument?: string) => {
    // execCommand 已被标记为废弃，但浏览器没有对应的富文本编辑替代 API，这里有意继续使用
    document.execCommand(command, false, argument) // NOSONAR 富文本加粗/斜体/引用没有替代 API
    emitMarkdown()
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <button
          type="button"
          className={styles.button}
          title="加粗"
          onMouseDown={(event) => {
            event.preventDefault()
            format('bold')
          }}
        >
          B
        </button>
        <button
          type="button"
          className={`${styles.button} ${styles.italic}`}
          title="斜体"
          onMouseDown={(event) => {
            event.preventDefault()
            format('italic')
          }}
        >
          I
        </button>
        <button
          type="button"
          className={styles.button}
          title="引用"
          onMouseDown={(event) => {
            event.preventDefault()
            format('formatBlock', 'blockquote')
          }}
        >
          “”
        </button>
      </div>
      {/* contentEditable 的 div 换成 <textarea> 会失去富文本，这里用 role 声明为文本框 */}
      <div // NOSONAR
        ref={editorRef}
        className={styles.editor}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-placeholder={placeholder}
        data-placeholder={placeholder}
        onFocus={() => {
          focusedRef.current = true
        }}
        onBlur={() => {
          focusedRef.current = false
          emitMarkdown()
        }}
        onInput={emitMarkdown}
      />
    </div>
  )
}
